import React from 'react';
import { FaFacebookF, FaYoutube, FaSoundcloud, FaXTwitter } from 'react-icons/fa6';
import { Instagram } from 'lucide-react';
import { SOCIALS, SocialId } from '@/config/site';
import { useNotice } from '@/hooks/useNotice';
import { isConfigured } from '@/lib/scroll';

export function SocialGlyph({ id, size }: { id: SocialId; size: number }) {
  switch (id) {
    case 'instagram':
      return <Instagram size={size} strokeWidth={2.1} />;
    case 'facebook':
      return <>{FaFacebookF({ size })}</>;
    case 'youtube':
      return <>{FaYoutube({ size })}</>;
    case 'soundcloud':
      return <>{FaSoundcloud({ size })}</>;
    case 'x':
      return <>{FaXTwitter({ size })}</>;
    default:
      return null;
  }
}

/** Anchor that honours unconfigured destinations (no fabricated links). */
export function ExtLink({
  url,
  label,
  className,
  style,
  testId,
  onFallback,
  onOpen,
  children,
}: {
  url: string;
  label: string;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  onFallback?: () => void;
  /** intercept a configured click (e.g. open an embedded player instead) */
  onOpen?: () => boolean;
  children: React.ReactNode;
}) {
  const { notify } = useNotice();
  const ok = isConfigured(url);
  return (
    <a
      href={ok ? url : '#'}
      target={ok ? '_blank' : undefined}
      rel={ok ? 'noopener noreferrer' : undefined}
      aria-label={label}
      className={className}
      style={style}
      data-testid={testId}
      data-configured={ok ? 'true' : 'false'}
      onClick={(e) => {
        if (onOpen && onOpen()) {
          e.preventDefault();
          return;
        }
        if (ok) return;
        e.preventDefault();
        if (onFallback) onFallback();
        else notify(`${label} link is not configured yet.`);
      }}
    >
      {children}
    </a>
  );
}

export function SocialLink({
  id,
  url,
  size,
  className,
  testPrefix,
}: {
  id: SocialId;
  url: string;
  size: number;
  className?: string;
  testPrefix: string;
}) {
  return (
    <ExtLink url={url} label={SOCIALS[id].label} className={className} testId={`${testPrefix}-${id}`}>
      <SocialGlyph id={id} size={size} />
    </ExtLink>
  );
}
